import React, { memo } from 'react';
import { Pressable } from 'react-native';

import { SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type RemoveCoGuardianButtonProps = {
  label: string;
  onPress: () => void;
};

/**
 * Destructive "Remove co-guardian" action. Rendered as a plain text+icon button on tablet
 * (sits under the right pane) and an outlined button on phone (pinned above the safe area).
 */
function RemoveCoGuardianButton({ label, onPress }: RemoveCoGuardianButtonProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [
        isTablet ? styles.textButton : styles.outlinedButton,
        pressed ? styles.pressed : null,
      ]}>
      <SvgIcon name="IcLeave" size={scale(20)} color={guardianColors.Error} />
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.label}>
        {label}
      </Text>
    </Pressable>
  );
}

export default memo(RemoveCoGuardianButton);

const useStyles = createStyles(({ scale }) => ({
  outlinedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    height: scale(48),
    padding: scale(10),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 0.5,
    borderColor: guardianColors.Error,
    backgroundColor: colors['0'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 2,
  },
  textButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    paddingVertical: scale(12),
  },
  pressed: {
    opacity: 0.7,
  },
  label: {
    color: guardianColors.Error,
  },
}));
